// earth/rig.js: the camera film for the earth chapter, a pure function of
// (progress, aspect, params, look). Geometry in Earth radii; the Sun is placed
// east of the visitor's city so the city sits ~12-16 deg into the night and the
// orbital sunrise happens straight ahead along the track (ISS flies east).
import * as THREE from 'three';
import { clamp, lerp, smooth, seg } from '../lib/common.js';

const D2R = Math.PI / 180;
export const R_ISS = 1 + 408 / 6371;      // ISS mean altitude ~408 km
const DECL = -1.8;                          // solar declination, late September
const HOUR = 106;                           // Sun this many degrees of longitude east of the city
export const SUN_R = 0.2666 * D2R;          // Sun's angular radius

export function dirLL(lat, lon, out = new THREE.Vector3()) {
  const a = lat * D2R, b = lon * D2R;
  return out.set(Math.cos(a) * Math.sin(b), Math.sin(a), Math.cos(a) * Math.cos(b));
}

// per-city frame: C city, T = local east (toward the Sun), B = C x T (north-ish).
// The Sun sits due east, DEPR deg below the city's horizon (equinox pre-dawn; artistic).
export const DEPR = 23.5;
export function makeFrame(lat, lon) {
  const C = dirLL(lat, lon);
  const T = new THREE.Vector3(C.z, 0, -C.x);
  if (T.lengthSq() < 1e-8) T.set(1, 0, 0);
  T.normalize();
  const B = new THREE.Vector3().crossVectors(C, T).normalize();
  const theta = DEPR * D2R;
  const S = C.clone().multiplyScalar(-Math.sin(theta)).addScaledVector(T, Math.cos(theta));
  return { C, S, T, B, theta, lat, lon };
}

const _v = new THREE.Vector3(), _w = new THREE.Vector3(), _u = new THREE.Vector3();
const nadir = (F, a, out) => out.copy(F.C).multiplyScalar(Math.cos(a)).addScaledVector(F.T, Math.sin(a));
const easeIO = (t) => t * t * (3 - 2 * t);

// orthonormal-ish blend of two unit vectors
function nlerp(a, b, t, out) { return out.copy(a).multiplyScalar(1 - t).addScaledVector(b, t).normalize(); }

// forward that looks toward the Sun on the limb, the limb sitting `phi` rad above screen centre
function limbLook(F, pos, phi, roll, fwd, up) {
  const r = pos.length(), dip = Math.acos(1 / r);
  _u.copy(pos).divideScalar(r);                                   // local vertical
  _v.copy(F.S).addScaledVector(_u, -F.S.dot(_u)).normalize();      // horizontal toward Sun
  const e = -dip - phi;                                            // elevation of the view axis
  fwd.copy(_v).multiplyScalar(Math.cos(e)).addScaledVector(_u, Math.sin(e)).normalize();
  up.copy(_u).addScaledVector(fwd, -_u.dot(fwd)).normalize();
  _w.crossVectors(fwd, up);
  up.multiplyScalar(Math.cos(roll)).addScaledVector(_w, Math.sin(roll)).normalize();
}

// view from az (0 = toward the Sun, PI = back toward the city) and elevation el, in the local frame at pos
function azEl(F, pos, az, el, roll, fwd, up) {
  _u.copy(pos).normalize();
  _v.copy(F.T).addScaledVector(_u, -F.T.dot(_u)).normalize();       // horizontal, toward the Sun
  _w.crossVectors(_u, _v);                                            // horizontal, side
  const ce = Math.cos(el);
  fwd.copy(_v).multiplyScalar(ce * Math.cos(az)).addScaledVector(_w, ce * Math.sin(az)).addScaledVector(_u, Math.sin(el)).normalize();
  up.copy(_u).addScaledVector(fwd, -_u.dot(fwd)).normalize();
  _w.crossVectors(fwd, up);
  up.multiplyScalar(Math.cos(roll)).addScaledVector(_w, Math.sin(roll)).normalize();
}

/* beats (progress):
   0.00-0.15  orbital sunrise over the limb, looking east (the ignition flash clears by ~0.1)
   0.15-0.38  swing 180 deg over the night side: dark limb, airglow, stars, scattered towns
   0.34-0.50  fly over the city, pitch down to a north-up near-nadir view, long-lens zoom (24 deg: ~170 km tall frame, ~2.4 px per 500 m texel)
   0.50-0.66  hold on the city (label), slow drift
   0.66-0.745 rise to the whole gibbous Earth (lit limb right) | 0.745-0.785 hold, whole disk in frame
   0.785-0.845 pull back to a point | 0.845-1.00 hold the pale blue dot (min-size point + Voyager band) */
export function rig(p, aspect, look, F, o) {
  const port = aspect < 1;
  const dip = Math.acos(1 / R_ISS), a0 = F.theta - dip;
  const kS = easeIO(seg(p, 0.15, 0.38)), kC = easeIO(seg(p, 0.34, 0.5)), kH = easeIO(seg(p, 0.5, 0.66));
  const a = lerp(a0 + (0.2 + 0.8 * (1 - Math.pow(1 - seg(p, 0, 0.16), 2)) - 0.6 * kS) * D2R, 0.35 * D2R, kC) - 0.25 * D2R * kH;
  const pos = nadir(F, a, o.pos).multiplyScalar(R_ISS);
  const fovA = port ? 74 : 46, fovS = port ? 80 : 58, fovC = port ? 27 : 24;
  azEl(F, pos, Math.PI * kS, lerp(-dip - (port ? 0.16 : 0.12) * fovA * D2R, -dip - 0.22 * fovS * D2R, kS),
    0.1 * (1 - kS) - 0.06 * Math.sin(Math.PI * kS), _a, _b);
  _c.copy(F.C).sub(pos).normalize();                                 // city look, north up
  _d.copy(F.B).addScaledVector(_c, -F.B.dot(_c)).normalize();
  nlerp(_a, _c, kC, o.fwd); nlerp(_b, _d, kC, o.up);
  let fov = lerp(lerp(fovA, fovS, kS), fovC, kC) * lerp(1, 0.9, kH);
  o.dist = R_ISS; o.sx = 0; o.sy = 0;
  if (p > 0.66) {
    // .66-.745 rise to the whole gibbous Earth | .745-.785 hold | .785-.845 pull back to a point | .845-1 hold the pale blue dot
    const fovE = port ? 62 : 36, tE = Math.tan(fovE * D2R / 2);
    const Dg = port ? Math.min(0.5, 0.78 * aspect) : Math.min(0.56, 0.42 * aspect);   // gibbous disk diameter / frame height
    const lG = 0.5 * Math.log(1 + 1 / ((Dg * tE) * (Dg * tE))), lD = Math.log(900 / (0.45 * tE));   // dot: ~0.2 px radius per 900 rows
    const lr = p < 0.745 ? lerp(Math.log(R_ISS), lG, easeIO(seg(p, 0.66, 0.745)))
      : lG + 0.16 * seg(p, 0.745, 0.785) + (lD - lG - 0.16) * easeIO(seg(p, 0.785, 0.845)) + 0.3 * seg(p, 0.845, 1);
    const r = Math.exp(lr);
    const dEnd = _h.copy(F.C).addScaledVector(F.T, 1.0).addScaledVector(F.B, 0.25).normalize();  // drift sunward: gibbous disk (~68% lit), lit limb right
    nlerp(_g.copy(pos).normalize(), dEnd, smooth(0.66, 0.76, p), _i).multiplyScalar(r);
    _j.copy(F.C).multiplyScalar(1 - smooth(0.66, 0.74, p));            // look target: city -> planet centre
    o.fwd.copy(_j).sub(_i).normalize();
    o.up.copy(F.B).addScaledVector(o.fwd, -F.B.dot(o.fwd)).normalize();
    o.pos.copy(_i);
    fov = lerp(fov, fovE, smooth(0.66, 0.75, p));
    o.dist = r;
    if (!port) o.sx = 0.06 * smooth(0.68, 0.76, p) * (1 - smooth(0.86, 0.95, p));   // desktop: planet right of centre (copy left), centred again for the cut; the shell's shiftX adds on top
  }
  // portrait: subject in the upper-middle; look sway
  _w.crossVectors(o.fwd, o.up).normalize();
  const tilt = port ? -0.1 * fov * D2R * smooth(0.3, 0.5, p) : 0;
  o.fwd.addScaledVector(o.up, tilt + 0.015 * look.y).addScaledVector(_w, 0.02 * look.x).normalize();
  o.up.addScaledVector(o.fwd, -o.up.dot(o.fwd)).normalize();
  o.fov = fov;
  o.flash = 1 - smooth(0.0, 0.1, p);
  return o;
}
const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _c = new THREE.Vector3(), _d = new THREE.Vector3(), _e = new THREE.Vector3();
const _f = new THREE.Vector3(), _g = new THREE.Vector3(), _h = new THREE.Vector3(), _i = new THREE.Vector3(), _j = new THREE.Vector3();

// fraction of the Sun's disk clear of the (opaque lower) atmosphere, seen from pos
export function sunVisible(pos, S) {
  const r = pos.length();
  const alpha = Math.acos(clamp(-pos.dot(S) / r, -1, 1));          // angle Sun <-> Earth centre
  const rho = Math.asin(Math.min(1, (1 + 12 / 6371) / r));          // ~12 km: where the limb goes opaque
  return smooth(-SUN_R, SUN_R, alpha - rho);
}
